import { useTranslation } from 'react-i18next'

import { Box, Text, VStack } from '@enechain/ecloud-designsystem'
import {
  FaInboxRegular,
  FaMagnifyingGlassRegular,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

type Props = {
  /**
   * フィルタが 1 つでも適用されているか。
   *
   * 「まだ作られていない」 と「絞り込んだ結果 0 件」 は**次の行動が違う**ので、
   * 空状態パネルを出す条件そのものになる (`design/ui-feasibility/components/
   * empty-panel.md`「いつ使うか」: 空の理由が複数あり理由ごとに行動が変わるなら
   * Alert ではなくパネル)。
   */
  isFiltered: boolean
}

/**
 * ワークフロー一覧の空状態パネル。
 *
 * 構造・トークンは `design/ui-feasibility/components/empty-panel.md` の使用トークン表に
 * 従う (値の正は DS `PageError`)。 同じ形の実装前例が [[NoDataGuide]] にあり、
 * layout もそこに揃える: icon 80px (`iconSize.emptyPanel`) /
 * title `emptyPanelTitle` (20px/600) / desc `body` + **`Text.highEmphasis`** (mid ではない) /
 * maxWidth 480px / `gap="2"` + `paddingX="4"` + `paddingY="10"`。
 *
 * **glyph で状態を描き分ける。** 同 md の対応表が「そもそも無い」 = 受信箱 /
 * 「絞り込みの結果が 0 件」 = 虫眼鏡 と定めている。 色は状態によらず
 * `Object.disabled` 固定 — 色に意味を持たせない (WCAG 1.4.1)。
 *
 * **受信箱側は「存在しない」と断定しない。** 既定のステータスフィルタは
 * `deriveWorkflowCaseStatuses('all', false, false)` = `[NOT_STARTED, IN_PROGRESS]` で、
 * **完了・削除済みは既定で API に要求していない**。 つまり 0 件でも「1 件も作られていない」
 * とは限らず、 完了済みしか無い組織では嘘になる。 断定を避けたうえで、 隠れている状態と
 * その出し方 (フィルタの「完了」「削除済み」) を本文で案内する。
 *
 * **パネル内に CTA は置かない。** 同 md の `__action` は既定 Tertiary だが、
 * 「テーブル空状態の CTA は委譲元 (帯側) と同じ variant」 という規約があり、 この画面の
 * 委譲元はページ見出し右の primary「ワークフロー追加」。 数 cm 上に出ている primary を
 * パネル内で複製すると同じ操作が 2 つ並ぶので、 導線は帯側に任せて本文で示す。
 *
 * **[design 差異]** 8080 の同画面 (`demand/cases.html`) は共通の表空行
 * (`js/table-empty-state.js` → `tr.ecl-empty-state-row` + 1 行テキスト) を使っており、
 * パネルではない。 実装は表ごと差し替える作りで「行だけ 0 件」 に当たらず、 かつ理由が
 * 2 通りあって行動が分かれるため、 辞書の分岐に従いパネルを採る。 プロト側の追随は別途。
 */
export const ListEmptyPanel = ({ isFiltered }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const suffix = isFiltered ? 'filtered' : 'notCreated'

  return (
    <VStack alignItems="center" gap="2" paddingX="4" paddingY="10">
      <Box color="Object.disabled">
        {isFiltered ? (
          <FaMagnifyingGlassRegular
            boxSize={iconSize.emptyPanel}
            aria-hidden="true"
          />
        ) : (
          <FaInboxRegular boxSize={iconSize.emptyPanel} aria-hidden="true" />
        )}
      </Box>

      <Text textStyle="emptyPanelTitle" color="Text.highEmphasis">
        {t(`page:workflowCases.list.${suffix}Title`)}
      </Text>

      <Text
        textStyle="body"
        maxWidth="480px"
        color="Text.highEmphasis"
        lineHeight={1.5}
        textAlign="center"
        whiteSpace="pre-line"
      >
        {t(`page:workflowCases.list.${suffix}Desc`)}
      </Text>
    </VStack>
  )
}
